/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, ReactNode, ReactElement } from 'react';

interface NavProps {
  children: ReactNode;
  toggle: string;
}

export const Nav = ({ children }: NavProps): ReactElement => {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const Logo = require('../../../assets/images/xencode.png');

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -80;
      const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setNavbarOpen(false);
  };

  const navLinkStyle = (id: string) =>
    `relative font-abeakrg text-lg cursor-pointer transition-all duration-300 ${
      activeSection === id
        ? 'text-xencode-gold after:w-full after:opacity-100'
        : 'text-white hover:text-xencode-gold after:w-0 hover:after:w-full after:opacity-60'
    } after:content-[""] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:bg-xencode-gold after:transition-all after:duration-300 after:shadow-[0_0_6px_#FFD700]`;

  useEffect(() => {
    const sectionIds = ['home', 'services', 'projects', 'about', 'contact'];
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop;
          const bottom = top + el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < bottom) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen w-full relative">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-xencode-contact-black shadow-md border-b-2 border-xencode-gold shadow-[0_2px_12px_rgba(255,215,0,0.3)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-24 py-4 flex items-center justify-between">
          {/* Logo */}
          <a onClick={() => scrollTo('home')} className="cursor-pointer">
            <img src={Logo} alt="Xencode" className="w-32 sm:w-36" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <a className={navLinkStyle('home')} onClick={() => scrollTo('home')}>
              Home
            </a>
            <a className={navLinkStyle('about')} onClick={() => scrollTo('about')}>
              About
            </a>
            <a className={navLinkStyle('services')} onClick={() => scrollTo('services')}>
              Services
            </a>
            <a className={navLinkStyle('projects')} onClick={() => scrollTo('projects')}>
              Projects
            </a>
            <button
              onClick={() => scrollTo('contact')}
              className="bg-xencode-gold text-black font-semibold rounded-full px-6 py-2 hover:bg-white hover:text-black transition shadow cursor-pointer"
            >
              Contact
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <div className="md:hidden">
            <button
              onClick={() => setNavbarOpen(!navbarOpen)}
              className="text-white focus:outline-none"
              aria-label="Toggle navigation"
            >
              {navbarOpen ? (
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Fullscreen Mobile Overlay Menu */}
      {navbarOpen && (
        <div className="fixed inset-0 bg-xencode-contact-black z-40 flex flex-col items-center justify-center space-y-8 text-white text-lg font-abeakrg">
          <a className={navLinkStyle('home')} onClick={() => scrollTo('home')}>
            Home
          </a>
          <a className={navLinkStyle('about')} onClick={() => scrollTo('about')}>
            About
          </a>
          <a className={navLinkStyle('services')} onClick={() => scrollTo('services')}>
            Services
          </a>
          <a className={navLinkStyle('projects')} onClick={() => scrollTo('projects')}>
            Projects
          </a>
          <button
            onClick={() => scrollTo('contact')}
            className="bg-xencode-gold text-black font-semibold rounded-full px-6 py-2 hover:bg-white hover:text-black transition shadow cursor-pointer"
          >
            Contact
          </button>
        </div>
      )}

      {/* Page Content */}
      <main className="pt-20">{children}</main>
    </div>
  );
};
